import { Box, Button } from '@mui/material';

import { QuestionLayout } from '../../components/QuestionLayout/QuestionLayout';
import { stringOrUndefined } from '../../storyblok/utils/stringOrUndefined';
import {
  AddCalendarEventButton as AddCalendarEventButtonCmp,
  LoadingAnimatedIcon,
  Text,
} from '@toolbox/components';
import { useLeadData, useWidgetContext } from '@widget/core';
import { useTimeslots } from '@widget/core/TimeSlotsProvider';
import { ConfirmationData } from '@widget/presets/BookAppointmentThankYouPage/ConfirmationData';
import { MUIWrapper } from '@widget/components';
import { renderComponent } from '@widget/storyblok/renderComponent';
import ThemeProvider from '@toolbox/style/theme/common/ThemeProvider';

import type { WidgetFormData } from '@widget/types/WidgetFormData';
import type {
  BookSalesAppointmentThankYouPageSchema,
  QuestionLayoutSchema,
} from '@widget/storyblok/schemas';
import type { Richtext } from 'storyblok-js-client';
import type { z } from 'zod';
type StepProps = z.infer<typeof BookSalesAppointmentThankYouPageSchema>;
type QuestionLayoutProps = z.infer<typeof QuestionLayoutSchema>;

const DEFAULT_TITLE = 'Ihr Termin ist bestätigt!' as const;
const DEFAULT_DESCRIPTION = `
        Einer unserer Makler wird sich in Kürze telefonisch bei Ihnen melden, um Ihre Bewertung und
        die Optionen für den Verkauf Ihrer Immobilie zu besprechen. Bitte prüfen Sie Ihren
        Posteingang und bestätigen Sie Ihre E-Mail Adresse. Anschließend erhalten Sie eine erste
        Kurzbewertung für Ihre Immobilie sowie Ihre Terminbestätigung.` as const;

export const BookSalesAppointmentThankYouPage: React.FC<StepProps> = ({
  AddCalendarEventButton,
  ...props
}) => {
  const questionLayoutProps = props.children[0] as QuestionLayoutProps;
  const { skip, ...addCalendarEventButton } = AddCalendarEventButton?.[0] || {};
  const widget = useWidgetContext();
  const { leadData } = useLeadData();
  const formData = widget.getFormData() as WidgetFormData;

  const { timezone } = useTimeslots();
  const timeslot = formData.bookSalesAppointmentTimeSlot;

  const handleClick = () => {
    widget.close();
  };

  const showAddCalendarEventButton = AddCalendarEventButton?.[0] && !skip && timeslot;

  return (
    <QuestionLayout
      key={questionLayoutProps._uid}
      back={questionLayoutProps.back}
      submit={questionLayoutProps.submit}
      skip={questionLayoutProps.skip}
      title={questionLayoutProps.title}
      description={questionLayoutProps.description}
      backButtonText={stringOrUndefined(questionLayoutProps.backButtonText)}
      closeButtonText={stringOrUndefined(questionLayoutProps.closeButtonText)}
      submitButtonText={stringOrUndefined(questionLayoutProps.submitButtonText)}
      skipButtonText={stringOrUndefined(questionLayoutProps.skipButtonText)}
      socialProofMessage={props.socialProofMessage as Richtext}
      maxW={!questionLayoutProps.maxW ? undefined : questionLayoutProps.maxW}
      headerType={props.headerType}
    >
      <div>
        {!leadData?.id || timeslot === undefined ? (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <LoadingAnimatedIcon />
          </Box>
        ) : (
          <>
            <Text fontWeight="bold" textAlign="center">
              {props.title || DEFAULT_TITLE}
            </Text>
            <Text mt={5} fontSize="18px">
              {props.description || DEFAULT_DESCRIPTION}
            </Text>
            <Text mt={5} mb={6} fontWeight="bold" textAlign="center" fontSize="20px">
              Termindetails
            </Text>
            <Box display={'flex'} justifyContent="center">
              <ConfirmationData
                isoString={timeslot}
                timezone={timezone}
                phone={formData?.contactForm?.phone}
                appointmentType={formData.bookingType}
                address={`${formData?.streetAndNumber ? `${formData.streetAndNumber}, ` : ''}${
                  formData?.zipCode ? `${formData.zipCode}, ` : ''
                }${formData?.city || ''}`}
              />
            </Box>
            {showAddCalendarEventButton && (
              <MUIWrapper showWhenListenerIsInactive={true} overlayHeight={140}>
                <Box
                  justifyContent="center"
                  mt={{ xs: 0, md: 3 }}
                  sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}
                >
                  <Box>
                    <AddCalendarEventButtonCmp
                      texts={addCalendarEventButton}
                      startTime={timeslot}
                      bookingType={formData.bookingType ? formData.bookingType : 'Call'}
                      address={`${formData?.streetAndNumber || ''}, ${formData?.zipCode || ''}, ${
                        formData?.city || ''
                      }`}
                    />
                  </Box>
                </Box>
              </MUIWrapper>
            )}
          </>
        )}
        {questionLayoutProps.children?.map(renderComponent)}
        <ThemeProvider>
          <Box sx={{ margin: '16px auto', maxWidth: { sm: '100%', md: '300px' } }}>
            <Button
              style={{ width: '100%' }}
              onClick={handleClick}
              variant={'outlined'}
              size={'small'}
            >
              Zurück zur Website
            </Button>
          </Box>
        </ThemeProvider>
      </div>
    </QuestionLayout>
  );
};
